import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaintBrushIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { EntityPalette } from './EntityPalette';
import { WordPalette } from './WordPalette';
import { EditorCanvas } from './EditorCanvas';
import { DimensionSelector } from './DimensionSelector';
import { EditorToolbar } from './EditorToolbar';
import { ValidationPanel } from './ValidationPanel';
import { CustomLevel, EditorAction, MAX_HISTORY } from '../../editor/EditorTypes';
import { validateLevel } from '../../editor/LevelValidator';
import { 
  downloadLevel, 
  saveToLocalStorage, 
  loadFromLocalStorage,
  saveDraft,
  loadDraft,
  importLevel 
} from '../../editor/LevelSerializer';
import { EntityData, WordBoxData } from '../../types';

const createEmptyLevel = (): CustomLevel => ({
  name: 'Untitled Level',
  description: 'A new custom level',
  author: 'Anonymous',
  version: '2.2.0',
  difficulty: 'medium',
  tags: [],
  created: new Date().toISOString(),
  entities: [],
  wordBoxes: {
    2: [],
    3: [],
    4: []
  }
});

export function LevelEditor() {
  const navigate = useNavigate();
  const [level, setLevel] = useState<CustomLevel>(createEmptyLevel());
  const [currentDimension, setCurrentDimension] = useState(1);
  const [selectedTool, setSelectedTool] = useState<'entity' | 'word' | 'erase' | 'select'>('entity');
  const [selectedEntity, setSelectedEntity] = useState<string | undefined>('PHANTOM');
  const [selectedWord, setSelectedWord] = useState<string | undefined>(undefined);
  const [history, setHistory] = useState<EditorAction[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isDirty, setIsDirty] = useState(false);
  const [validation, setValidation] = useState(validateLevel(level));

  // Auto-validate on level changes
  useEffect(() => {
    setValidation(validateLevel(level));
    if (history.length > 0) {
      setIsDirty(true);
      saveDraft(level);
    }
  }, [level, history.length]);

  // Load draft on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft) {
      const loadDraftChoice = window.confirm('Load previously saved draft?');
      if (loadDraftChoice) {
        setLevel(draft);
      }
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'z':
            e.preventDefault();
            handleUndo();
            break;
          case 'y':
            e.preventDefault();
            handleRedo();
            break;
          case 's':
            e.preventDefault();
            handleSave();
            break;
          case 't':
            e.preventDefault();
            handleTest();
            break;
          case 'n':
            e.preventDefault();
            handleNew();
            break;
        }
      } else if (e.key >= '1' && e.key <= '4') {
        setCurrentDimension(parseInt(e.key));
      } else if (e.key === 'Escape') {
        navigate('/');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [history, historyIndex, navigate]);

  const addToHistory = useCallback((action: EditorAction) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(action);
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
      }
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [historyIndex]);

  const handleCanvasClick = useCallback((x: number, y: number) => {
    if (selectedTool === 'entity' && currentDimension === 1 && selectedEntity) {
      // Add entity
      const newEntity: EntityData = { type: selectedEntity, x, y };
      setLevel(prev => ({
        ...prev,
        entities: [...prev.entities, newEntity]
      }));
      addToHistory({
        type: 'ADD_ENTITY',
        data: newEntity,
        timestamp: Date.now()
      });
    } else if (selectedTool === 'word' && currentDimension > 1 && selectedWord) {
      // Add word box
      const newWord: WordBoxData = { type: selectedWord, x, y };
      setLevel(prev => ({
        ...prev,
        wordBoxes: {
          ...prev.wordBoxes,
          [currentDimension]: [...(prev.wordBoxes[currentDimension] || []), newWord]
        }
      }));
      addToHistory({
        type: 'ADD_WORD',
        data: { dimension: currentDimension, word: newWord },
        timestamp: Date.now()
      });
    }
  }, [selectedTool, currentDimension, selectedEntity, selectedWord, addToHistory]);

  const handleCanvasRightClick = useCallback((x: number, y: number) => {
    if (currentDimension === 1) {
      // Remove entity at position
      setLevel(prev => ({
        ...prev,
        entities: prev.entities.filter(e => e.x !== x || e.y !== y)
      }));
    } else {
      // Remove word box at position
      setLevel(prev => ({
        ...prev,
        wordBoxes: {
          ...prev.wordBoxes,
          [currentDimension]: (prev.wordBoxes[currentDimension] || []).filter(
            w => w.x !== x || w.y !== y
          )
        }
      }));
    }
  }, [currentDimension]);

  const handleSelectEntity = useCallback((entityType: string) => {
    setSelectedEntity(entityType);
    setSelectedWord(undefined);
    setSelectedTool('entity');
  }, []);

  const handleSelectWord = useCallback((wordType: string) => {
    setSelectedWord(wordType);
    setSelectedEntity(undefined);
    setSelectedTool('word');
  }, []);

  const handleNew = useCallback(() => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) {
      return;
    }
    setLevel(createEmptyLevel());
    setHistory([]);
    setHistoryIndex(-1);
    setIsDirty(false);
  }, [isDirty]);

  const handleSave = useCallback(() => {
    try {
      saveToLocalStorage(level);
      setIsDirty(false);
      alert('Level saved successfully!');
    } catch (error) {
      alert('Failed to save: ' + (error as Error).message);
    }
  }, [level]);

  const handleLoad = useCallback(() => {
    const levels = loadFromLocalStorage();
    if (levels.length === 0) {
      alert('No saved levels found');
      return;
    }
    
    const levelNames = levels.map((l, i) => `${i + 1}. ${l.name}`).join('\n');
    const choice = prompt(`Select level to load:\n${levelNames}\n\nEnter number:`);
    
    if (choice) {
      const index = parseInt(choice) - 1;
      if (index >= 0 && index < levels.length) {
        setLevel(levels[index]);
        setHistory([]);
        setHistoryIndex(-1);
        setIsDirty(false);
      }
    }
  }, []);

  const handleTest = useCallback(() => {
    if (!validation.valid) {
      alert('Cannot test invalid level. Please fix errors first.');
      return;
    }
    alert('Test mode will be implemented in the next phase!');
  }, [validation]);

  const handleExport = useCallback(() => {
    try {
      downloadLevel(level);
      alert('Level exported successfully!');
    } catch (error) {
      alert('Failed to export: ' + (error as Error).message);
    }
  }, [level]);

  const handleImport = useCallback(() => {
    const json = prompt('Paste level JSON:');
    if (json) {
      try {
        const importedLevel = importLevel(json);
        setLevel(importedLevel);
        setHistory([]);
        setHistoryIndex(-1);
        setIsDirty(true);
        alert('Level imported successfully!');
      } catch (error) {
        alert('Failed to import: ' + (error as Error).message);
      }
    }
  }, []);

  const handleUndo = useCallback(() => {
    if (historyIndex >= 0) {
      setHistoryIndex(prev => prev - 1);
      // TODO: Implement proper undo logic
    }
  }, [historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      // TODO: Implement proper redo logic
    }
  }, [historyIndex, history.length]);

  return (
    <div className="level-editor">
      <header className="editor-header">
        <h1><PaintBrushIcon style={{ width: '20px', height: '20px', display: 'inline-block', verticalAlign: 'middle', marginRight: '8px' }} />Level Editor - {level.name}</h1>
        <button onClick={() => navigate('/')} className="close-btn">
          <XMarkIcon style={{ width: '18px', height: '18px' }} /> Close
        </button>
      </header>

      <div className="editor-content">
        <aside className="editor-sidebar">
          <EntityPalette
            selectedEntity={selectedEntity}
            onSelectEntity={handleSelectEntity}
          />
          <WordPalette
            selectedWord={selectedWord}
            onSelectWord={handleSelectWord}
          />
          <ValidationPanel validation={validation} />
        </aside>

        <main className="editor-main">
          <div style={{ display: 'inline-block', verticalAlign: 'top' }}>
            <EditorCanvas
              entities={level.entities}
              wordBoxes={level.wordBoxes}
              currentDimension={currentDimension}
              selectedTool={selectedTool}
              selectedEntity={selectedEntity}
              selectedWord={selectedWord}
              onCanvasClick={handleCanvasClick}
              onCanvasRightClick={handleCanvasRightClick}
            />
            
            <DimensionSelector
              currentDimension={currentDimension}
              onDimensionChange={setCurrentDimension}
            />
          </div>
        </main>
      </div>

      <EditorToolbar
        onNew={handleNew}
        onSave={handleSave}
        onLoad={handleLoad}
        onTest={handleTest}
        onExport={handleExport}
        onImport={handleImport}
        canUndo={historyIndex >= 0}
        canRedo={historyIndex < history.length - 1}
        onUndo={handleUndo}
        onRedo={handleRedo}
        isDirty={isDirty}
      />
    </div>
  );
}
